import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { DATA_INVITES_LOADED_EVENT, DriverMarkerState, InviteView, Map, MarkerRenderers, mergeBounds } from '@glympse/map-core';
import Glympser, { GlympserMarker, MapViewerInvite } from '../../shared/components/Glympser/Glympser';
import { JobPhase } from '../../shared/models/jobs';
import destinationIcon from '../../assets/images/destination-marker.png';
import './ActiveJobsMap.scss';

const getStatusColor = (phase) => ([JobPhase.Pre, JobPhase.Scheduled].includes(phase) ? '#d9d9d9' : '#2eb85c');

const getInitials = (name?: string) => (name && name.replace(/\W*(\w)\w*/g, '$1').toUpperCase().substring(0, 2)) || '--';

const getMarkerScale = (zoom: number) => 0.5 + 0.5 * Math.max(0, Math.min(1, ((zoom + 1 - 5) / 13) ** 2));

const renderDriverMarker = (state: DriverMarkerState) => {
  const el = document.createElement('div');
  el.className = `status-marker${state.selected ? ' selected' : ''}`;
  el.textContent = getInitials(state.name);

  const status = document.createElement('span');
  status.className = 'status';
  status.style.background = String(state.data.statusColor || '#777777');
  el.appendChild(status);

  return el;
};

const markerRenderers: MarkerRenderers = {
  driver: { icon: false, render: renderDriverMarker },
  destination: { icon: { src: destinationIcon, width: 41, height: 52 }, render: false },
};

const toInviteData = (view: InviteView, lastUpdateTime: number): MapViewerInvite => {
  const eta = view.propMap?.eta as any;
  const location = view.driverLocation;

  return {
    id: view.inviteCode,
    status: view.expired ? 'expired' : 'active',
    location: location ? { lt: location.latitude, ln: location.longitude } : null,
    lastUpdateTime,
    eta: eta?.eta ? eta.eta / 1000 : null,
    custom: [...(view.invite?.properties || []), ...(view.invite?.data || [])],
  };
};

type Task = any;

export interface ActiveJobsMapConfig {
  area?: {
    centerLat: number;
    centerLng: number;
    allowMarkers?: boolean;
    markers?: { lat: number; lng: number; ext?: { w: number; h: number; path: string } }[];
  };
}

interface ActiveJobsMapProps {
  activeTasks: Task[];
  selectedJob?: Task;
  onSelectJob: (invite) => void;
  showDestOnSelect?: boolean;
  onInviteListChanged?: (invites) => void;
  config?: ActiveJobsMapConfig;
}

const ActiveJobsMap = (props: ActiveJobsMapProps) => {
  const { activeTasks = [], onSelectJob, selectedJob, showDestOnSelect, onInviteListChanged, config } = props;
  const [map, setMap] = useState<Map>(null);
  const [invites, setInvites] = useState([]);

  const selectedCode = showDestOnSelect ? selectedJob?.invite_code : undefined;

  const phasesRef = useRef<Record<string, string>>({});
  const selectedCodeRef = useRef<string>();
  const prevSelectedCodeRef = useRef<string>(selectedCode);
  const onSelectJobRef = useRef(onSelectJob);
  const lastUpdatesRef = useRef<Record<string, number>>({});
  const clickableRef = useRef(new WeakSet<object>());

  phasesRef.current = (activeTasks || []).filter((t) => t.invite_code).reduce((acc, t) => ({ ...acc, [t.invite_code]: t.phase }), {});
  const phasesKey = JSON.stringify(phasesRef.current);
  const inviteIds = useMemo(() => Object.keys(phasesRef.current), [phasesKey]);
  selectedCodeRef.current = selectedCode;
  onSelectJobRef.current = onSelectJob;

  const getViews = useCallback(() => map?.getInviteController().getInviteViews() || [], [map]);

  const isHidden = (view: InviteView) => !!selectedCodeRef.current && view.inviteCode !== selectedCodeRef.current;

  const applyView = useCallback((view: InviteView) => {
    const statusColor = getStatusColor(phasesRef.current[view.inviteCode]);
    if (view.markerData.statusColor !== statusColor) {
      view.setMarkerData({ statusColor });
    }

    const marker = view.driverMarker;
    if (marker && !clickableRef.current.has(marker)) {
      clickableRef.current.add(marker);
      marker.on('click', () => onSelectJobRef.current(view.inviteCode));
    }
    if (isHidden(view)) {
      marker?.setVisible(false);
    }

    const trailLayerId = `invite-${view.inviteCode}-trail`;
    if (map.getLayer(trailLayerId)) {
      map.setLayoutProperty(trailLayerId, 'visibility', 'none');
    }
    const routeLayerId = `invite-${view.inviteCode}-route`;
    if (map.getLayer(routeLayerId)) {
      map.setPaintProperty(routeLayerId, 'circle-opacity', 0.7);
    }
  }, [map]);

  const fitVisible = useCallback(() => {
    const selected = getViews().find((view) => view.inviteCode === selectedCodeRef.current);
    const bounds = selected
      ? mergeBounds([selected.driverMarker?.getBounds(), selected.destinationMarker?.getBounds()])
      : mergeBounds([
        map.getFeatureBounds(),
        ...getViews().map((view) => view.driverMarker?.getBounds()),
      ]);
    if (bounds) map.fitBounds(bounds, { padding: 60, maxZoom: selected ? 17 : 15 });
  }, [map, getViews]);

  const syncSelection = useCallback(() => {
    getViews().forEach((view) => {
      const isSelected = view.inviteCode === selectedCodeRef.current;
      if (view.selected !== isSelected) {
        if (isSelected) {
          view.selectView();
        } else {
          view.deselectView();
        }
      }
      view.driverMarker?.setVisible(!!view.driverLocation && !isHidden(view));
    });
  }, [getViews]);

  const emitInvites = useCallback(() => {
    setInvites(getViews().map((view) => {
      const data = toInviteData(view, lastUpdatesRef.current[view.inviteCode]);
      return { getInvite: () => data };
    }));
  }, [getViews]);

  useEffect(() => {
    if (!map) return undefined;

    const onData = ({ inviteView }: { inviteView: InviteView }) => {
      const location = inviteView.invite?.location;
      if (location?.length) {
        lastUpdatesRef.current[inviteView.inviteCode] = location.reduce((ts, point) => ts + (point[0] || 0), 0);
      }
      applyView(inviteView);
      emitInvites();
    };

    const onSourceData = (e) => {
      const view = e.sourceId && getViews().find((v) => e.sourceId === `invite-${v.inviteCode}`);
      if (view) applyView(view);
    };

    const onZoom = () => {
      map.getContainer().style.setProperty('--marker-scale', String(getMarkerScale(map.getZoom())));
    };
    onZoom();

    map.on('zoom', onZoom);
    map.on('inviteview:data', onData);
    map.on('inviteview:select', syncSelection);
    map.on('sourcedata', onSourceData);
    map.on(DATA_INVITES_LOADED_EVENT, fitVisible);

    return () => {
      map.off('zoom', onZoom);
      map.off('inviteview:data', onData);
      map.off('inviteview:select', syncSelection);
      map.off('sourcedata', onSourceData);
      map.off(DATA_INVITES_LOADED_EVENT, fitVisible);
    };
  }, [map, applyView, syncSelection, emitInvites, getViews, fitVisible]);

  useEffect(() => {
    getViews().forEach(applyView);
    emitInvites();
  }, [phasesKey, getViews]);

  useEffect(() => {
    syncSelection();
    if (map && prevSelectedCodeRef.current !== selectedCode) {
      prevSelectedCodeRef.current = selectedCode;
      fitVisible();
    }
  }, [selectedCode, syncSelection]);

  useEffect(() => {
    if (onInviteListChanged) {
      onInviteListChanged(invites);
    }
  }, [invites]);

  const area = config?.area;
  const center = area ? { lat: area.centerLat, lng: area.centerLng } : undefined;
  const markers: GlympserMarker[] = (area?.allowMarkers && area.markers || []).map((marker) => ({
    lat: marker.lat,
    lng: marker.lng,
    icon: marker.ext && { src: marker.ext.path, width: marker.ext.w, height: marker.ext.h },
  }));

  return (
    <Glympser
      invites={inviteIds}
      center={center}
      markers={markers}
      markerRenderers={markerRenderers}
      onMapReady={setMap}
    />
  );
};

ActiveJobsMap.propTypes = {
  activeTasks: PropTypes.arrayOf(Object),
};

ActiveJobsMap.defaultProps = {
  activeTasks: [],
  selectedJob: null,
  showDestOnSelect: false,
  onInviteListChanged: () => {},
  config: {},
}

export default ActiveJobsMap;
